import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatCurrency } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { BadgeCheck, Trash2, Sparkles, Download, Upload, FileSpreadsheet, Plus, Pencil, Save, X } from "lucide-react";
import { API } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { RichTextEditor } from "../components/RichTextEditor";

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [biz, setBiz] = useState([]);
  const [users, setUsers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [editBiz, setEditBiz] = useState(null);

  const isAuthorized = user && (user.role === "admin" || user.role === "manager" || user.role === "editor");
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const isEditor = user?.role === "editor";
  const canManageUsers = isAdmin || isManager;        // view, edit (non-role), grant credits
  const canChangeRoles = isAdmin;                      // promote/demote
  const canDeleteUsers = isAdmin;
  const canSeeFinancials = isAdmin || isManager;       // invoices, revenue stats
  const canManageCoupons = isAdmin || isManager;       // create/edit; delete = admin only
  const canDeleteCoupon = isAdmin;
  const canEditSiteSettings = isAdmin;
  const canCsvOps = isAdmin || isManager;
  const canCreateListingForUser = isAdmin || isManager;
  const canModerateListings = isAdmin || isManager;    // verify/status/delete listing
  // editor: content-only — edit listings + CMS pages

  useEffect(() => {
    if (user === false) nav("/login", { state: { from: "/admin" } });
    else if (user && !isAuthorized) nav("/dashboard");
  }, [user, isAuthorized, nav]);

  const loadAll = useCallback(() => {
    const tasks = [api.get("/admin/businesses").then((r) => setBiz(r.data))];
    if (canSeeFinancials) {
      tasks.push(api.get("/admin/stats").then((r) => setStats(r.data)));
      tasks.push(api.get("/admin/invoices").then((r) => setInvoices(r.data)));
    }
    if (canManageUsers) {
      tasks.push(api.get("/admin/users").then((r) => setUsers(r.data)));
    }
    return Promise.all(tasks);
  }, [canSeeFinancials, canManageUsers]);
  useEffect(() => { if (isAuthorized) loadAll(); }, [isAuthorized, loadAll]);

  if (!isAuthorized) return null;

  const setStatus = async (id, status) => {
    await api.post(`/admin/businesses/${id}/status?status=${status}`);
    toast.success("Status updated");
    loadAll();
  };
  const toggleVerify = async (id, v) => { await api.post(`/admin/businesses/${id}/verify?verified=${v}`); loadAll(); };
  const remove = async (id) => { if (!window.confirm("Delete listing?")) return; await api.delete(`/admin/businesses/${id}`); toast.success("Deleted"); loadAll(); };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-10" data-testid="admin-page">
      <div className="mb-8">
        <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-2">/ Admin</div>
        <h1 className="font-display text-4xl tracking-tight">Operations</h1>
        <p className="text-sm text-zinc-500 mt-1">Listings, users, invoices and platform health.</p>
      </div>

      {stats && canSeeFinancials && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          <Stat label="Users" value={stats.users_total} />
          <Stat label="Businesses" value={`${stats.businesses_active}/${stats.businesses_total}`} hint="active / total" />
          <Stat label="Sponsored now" value={stats.sponsored} />
          <Stat label="Revenue" value={formatCurrency(stats.revenue_cents)} accent />
          <Stat label="Pro members" value={stats.pro_members} />
          <Stat label="Premium members" value={stats.premium_members} />
        </div>
      )}
      {isEditor && (
        <div className="mb-8 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm" data-testid="editor-scope-banner">
          <strong>Editor access.</strong> You can edit business listings and CMS pages. User management, financials, coupons, CSV import/export and site settings are restricted to managers and admins.
        </div>
      )}

      <Tabs defaultValue="listings">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="listings" data-testid="admin-tab-listings">Listings</TabsTrigger>
          {canManageUsers && (
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          )}
          {canSeeFinancials && (
            <TabsTrigger value="invoices" data-testid="admin-tab-invoices">Invoices</TabsTrigger>
          )}
          {canManageCoupons && (
            <TabsTrigger value="coupons" data-testid="admin-tab-coupons">Coupons</TabsTrigger>
          )}
          <TabsTrigger value="pages" data-testid="admin-tab-pages">Pages</TabsTrigger>
          {(canEditSiteSettings || isManager) && (
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Site settings</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="listings" className="mt-6 space-y-4">
          {canCsvOps && <CsvToolbar onImported={loadAll} />}
          {canCreateListingForUser && <AdminCreateForUser users={users} onCreated={loadAll} />}
          <Card>
            <CardHeader><CardTitle className="font-display">All listings</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-xs">
                  <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Category</th><th className="text-left p-3">Plan</th><th className="text-left p-3">Status</th><th className="text-left p-3">Stats</th><th className="text-right p-3">Actions</th></tr>
                </thead>
                <tbody>
                  {biz.map((b) => (
                    <tr key={b.id} className="border-t border-zinc-100" data-testid={`admin-biz-${b.id}`}>
                      <td className="p-3"><Link to={`/business/${b.id}`} className="font-medium hover:underline">{b.name}</Link>{b.verified && <BadgeCheck className="inline h-4 w-4 text-blue-600 ml-1" />}{b.sponsored_until && new Date(b.sponsored_until) > new Date() && <Sparkles className="inline h-3.5 w-3.5 text-amber-500 ml-1" />}</td>
                      <td className="p-3 font-mono-tag text-xs text-zinc-500">{b.category_slug}</td>
                      <td className="p-3"><Badge variant="outline">{b.plan}</Badge></td>
                      <td className="p-3">
                        {canModerateListings ? (
                          <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
                            <SelectTrigger className="h-8 w-32" data-testid={`admin-status-${b.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">active</SelectItem>
                              <SelectItem value="pending">pending</SelectItem>
                              <SelectItem value="suspended">suspended</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" data-testid={`admin-status-readonly-${b.id}`}>{b.status}</Badge>
                        )}
                      </td>
                      <td className="p-3 text-xs text-zinc-500 font-mono-tag">{b.views}v · {b.clicks}c</td>
                      <td className="p-3 text-right space-x-2">
                        <Button size="sm" variant="outline" onClick={() => setEditBiz(b)} data-testid={`admin-edit-biz-${b.id}`}><Pencil className="h-4 w-4" /></Button>
                        {canModerateListings && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => toggleVerify(b.id, !b.verified)} data-testid={`admin-verify-${b.id}`}>{b.verified ? "Unverify" : "Verify"}</Button>
                            <Button size="sm" variant="outline" onClick={() => remove(b.id)} data-testid={`admin-del-${b.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {canManageUsers && (
          <TabsContent value="users" className="mt-6">
            <UsersPanel users={users} reload={loadAll} canChangeRoles={canChangeRoles} canDeleteUsers={canDeleteUsers} />
          </TabsContent>
        )}

        {canSeeFinancials && (
          <TabsContent value="invoices" className="mt-6">
            <Card>
              <CardHeader><CardTitle className="font-display">All invoices</CardTitle></CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-xs">
                    <tr><th className="text-left p-3">Date</th><th className="text-left p-3">User</th><th className="text-left p-3">Item</th><th className="text-left p-3">Status</th><th className="text-right p-3">Amount</th></tr>
                  </thead>
                  <tbody>
                    {invoices.map((i) => (
                      <tr key={i.id} className="border-t border-zinc-100" data-testid={`admin-inv-${i.id}`}>
                        <td className="p-3 font-mono-tag text-zinc-500 text-xs">{i.created_at?.slice(0,10)}</td>
                        <td className="p-3">{i.user_name} <span className="text-zinc-500 text-xs">({i.user_email})</span></td>
                        <td className="p-3">{i.item}</td>
                        <td className="p-3"><Badge variant="outline" className="border-emerald-200 text-emerald-700">{i.status}</Badge></td>
                        <td className="p-3 text-right font-mono-tag">{formatCurrency(i.amount_cents, i.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="pages" className="mt-6">
          <PagesPanel canDelete={canModerateListings} />
        </TabsContent>

        {canManageCoupons && (
          <TabsContent value="coupons" className="mt-6">
            <CouponsPanel canDelete={canDeleteCoupon} />
          </TabsContent>
        )}

        {(canEditSiteSettings || isManager) && (
          <TabsContent value="settings" className="mt-6">
            <SiteSettingsPanel readOnly={!canEditSiteSettings} />
          </TabsContent>
        )}
      </Tabs>
      <BusinessEditDialog biz={editBiz} onClose={() => setEditBiz(null)} onSaved={loadAll} />
    </div>
  );
}

function Stat({ label, value, hint, accent }) {
  return (
    <div className={`rounded-lg border ${accent ? "border-zinc-900 surface-navy" : "border-zinc-200 bg-white"} p-5`}>
      <div className={`text-xs uppercase tracking-[0.18em] ${accent ? "text-zinc-400" : "text-zinc-500"} font-mono-tag`}>{label}</div>
      <div className="font-display text-2xl mt-2">{value}</div>
      {hint && <div className="text-[10px] mt-1 text-zinc-500 font-mono-tag">{hint}</div>}
    </div>
  );
}

function CsvToolbar({ onImported }) {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const fileRef = React.useRef(null);

  const downloadAuth = async (path, fallbackName) => {
    const tok = localStorage.getItem("bizzatlas_token");
    const res = await fetch(`${API}${path}`, {
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      credentials: "include",
    });
    if (!res.ok) { toast.error("Download failed"); return; }
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="([^"]+)"/);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = m ? m[1] : fallbackName;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const tok = localStorage.getItem("bizzatlas_token");
      const res = await fetch(`${API}/admin/businesses/import`, {
        method: "POST",
        body: fd,
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        credentials: "include",
      });
      const json = await res.json();
      if (!res.ok) {
        toast.error(json.detail || "Import failed");
      } else {
        setResult(json);
        toast.success(`Imported: ${json.created} created · ${json.updated} updated · ${json.errors.length} errors`);
        onImported && onImported();
      }
    } catch (err) {
      toast.error(err.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="csv-toolbar">
      <CardContent className="p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md grid place-items-center surface-navy">
              <FileSpreadsheet className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg leading-tight">Bulk CSV import / export</div>
              <div className="text-xs text-zinc-500">Rows with an existing <code className="font-mono-tag">id</code> update in place. New rows resolve owners via <code className="font-mono-tag">owner_email</code>.</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => downloadAuth("/admin/businesses/import/template", "bizzatlas-listings-template.csv")} data-testid="csv-template-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Template
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadAuth("/admin/businesses/export", "bizzatlas-listings.csv")} data-testid="csv-export-btn">
              <Download className="h-4 w-4 mr-1.5" /> Export all
            </Button>
            <Button size="sm" className="btn-brand" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="csv-import-btn">
              <Upload className="h-4 w-4 mr-1.5" /> {busy ? "Uploading…" : "Import CSV"}
            </Button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={onPick} data-testid="csv-import-input" />
          </div>
        </div>
        {result && (
          <div className="mt-4 rounded-md border border-zinc-200 bg-zinc-50/60 p-4 text-sm" data-testid="csv-import-result">
            <div className="flex items-center gap-6 mb-2">
              <span><b className="text-emerald-700">{result.created}</b> created</span>
              <span><b className="text-blue-700">{result.updated}</b> updated</span>
              <span><b className={result.errors.length ? "text-red-600" : "text-zinc-700"}>{result.errors.length}</b> errors</span>
            </div>
            {result.errors.length > 0 && (
              <div className="mt-2 max-h-40 overflow-auto scroll-soft">
                <table className="w-full text-xs">
                  <thead className="text-zinc-500 uppercase tracking-wider">
                    <tr><th className="text-left p-1">Row</th><th className="text-left p-1">Error</th></tr>
                  </thead>
                  <tbody>
                    {result.errors.map((er, i) => (
                      <tr key={i} className="border-t border-zinc-100"><td className="p-1 font-mono-tag">{er.row}</td><td className="p-1 text-red-700">{er.error}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PagesPanel({ canDelete = true }) {
  const [pages, setPages] = React.useState([]);
  const [editing, setEditing] = React.useState(null); // page object or {} for new
  const load = () => api.get("/admin/pages").then((r) => setPages(r.data));
  React.useEffect(() => { load(); }, []);

  const save = async (p) => {
    try {
      if (p._isNew) {
        await api.post("/admin/pages", {
          slug: p.slug, title: p.title, body_md: p.body_md, published: !!p.published,
          meta_title: p.meta_title || null, meta_description: p.meta_description || null,
        });
        toast.success("Page created");
      } else {
        await api.patch(`/admin/pages/${p.slug}`, {
          title: p.title, body_md: p.body_md, published: !!p.published,
          meta_title: p.meta_title || null, meta_description: p.meta_description || null,
        });
        toast.success("Page saved");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const remove = async (slug) => {
    if (!window.confirm(`Delete /${slug}?`)) return;
    await api.delete(`/admin/pages/${slug}`);
    toast.success("Deleted");
    load();
  };

  return (
    <Card data-testid="admin-pages-panel">
      <CardHeader>
        <CardTitle className="font-display flex items-center justify-between">
          Static pages
          <Button size="sm" onClick={() => setEditing({ _isNew: true, slug: "", title: "", body_md: "", published: true })} className="btn-brand" data-testid="admin-pages-new">
            <Plus className="h-4 w-4 mr-1" /> New page
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {editing ? (
          <PageEditor page={editing} onCancel={() => setEditing(null)} onSave={save} />
        ) : (
          <div className="divide-y divide-zinc-100">
            {pages.length === 0 && <div className="text-sm text-zinc-500 py-6">No pages yet.</div>}
            {pages.map((p) => (
              <div key={p.slug} className="py-3 flex items-center justify-between" data-testid={`admin-page-row-${p.slug}`}>
                <div>
                  <div className="font-medium flex items-center gap-2">
                    /{p.slug}
                    {!p.published && <Badge variant="outline" className="border-amber-200 text-amber-700">draft</Badge>}
                  </div>
                  <div className="text-xs text-zinc-500">{p.title}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...p })} data-testid={`admin-page-edit-${p.slug}`}><Pencil className="h-4 w-4" /></Button>
                  {canDelete && (
                    <Button size="sm" variant="outline" onClick={() => remove(p.slug)} data-testid={`admin-page-del-${p.slug}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PageEditor({ page, onCancel, onSave }) {
  const [form, setForm] = React.useState(page);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-4" data-testid="admin-page-editor">
      <div className="flex items-center justify-between">
        <div className="font-display text-2xl">{form._isNew ? "New page" : `Edit /${form.slug}`}</div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onCancel}><X className="h-4 w-4 mr-1" />Cancel</Button>
          <Button size="sm" onClick={() => onSave(form)} className="btn-brand" data-testid="admin-page-save"><Save className="h-4 w-4 mr-1" />Save</Button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Slug</Label>
          <Input value={form.slug} onChange={(e) => set("slug", e.target.value)} disabled={!form._isNew} placeholder="about" data-testid="admin-page-slug" />
        </div>
        <div className="space-y-2">
          <Label>Title</Label>
          <Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="admin-page-title" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Body</Label>
        <RichTextEditor
          value={form.body_md || ""}
          onChange={(html) => set("body_md", html)}
          placeholder="Write the page content. Use the toolbar to add headings, links, lists, quotes, and more."
          testid="admin-page-body"
        />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Meta title (SEO)</Label>
          <Input value={form.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} maxLength={70} data-testid="admin-page-meta-title" />
        </div>
        <div className="space-y-2">
          <Label>Meta description (SEO)</Label>
          <Input value={form.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} maxLength={160} data-testid="admin-page-meta-desc" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={!!form.published} onCheckedChange={(v) => set("published", v)} data-testid="admin-page-published" />
        <Label className="cursor-pointer">{form.published ? "Published" : "Draft"}</Label>
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Users panel — edit, suspend, delete, grant credits
// ---------------------------------------------------------------------------
function UsersPanel({ users, reload, canChangeRoles = true, canDeleteUsers = true }) {
  const [grant, setGrant] = React.useState(null);   // user being granted credits
  const [editing, setEditing] = React.useState(null);

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This wipes their listings, messages and campaigns.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display">Users</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-xs">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Plan</th>
              <th className="text-left p-3">Verified</th>
              <th className="text-left p-3">Credits</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-zinc-100" data-testid={`admin-user-${u.id}`}>
                <td className="p-3">{u.name}</td>
                <td className="p-3 text-zinc-500">{u.email}</td>
                <td className="p-3"><Badge variant="outline">{u.role}</Badge></td>
                <td className="p-3"><Badge variant="outline">{u.membership}</Badge></td>
                <td className="p-3"><Badge variant="outline" className={u.email_verified ? "border-emerald-200 text-emerald-700" : "border-amber-200 text-amber-700"}>{u.email_verified ? "yes" : "no"}</Badge></td>
                <td className="p-3 font-mono-tag text-xs">{u.ad_credits}</td>
                <td className="p-3 text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => setGrant(u)} data-testid={`admin-grant-${u.id}`}>+ Credits</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...u })} data-testid={`admin-edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                  {canDeleteUsers && (
                    <Button size="sm" variant="outline" onClick={() => del(u)} disabled={u.role === "admin"} data-testid={`admin-del-user-${u.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      <GrantCreditsDialog open={!!grant} user={grant} onClose={() => { setGrant(null); reload(); }} />
      <EditUserDialog open={!!editing} user={editing} onClose={() => { setEditing(null); reload(); }} canChangeRoles={canChangeRoles} />
    </Card>
  );
}

function GrantCreditsDialog({ open, user, onClose }) {
  const [credits, setCredits] = React.useState(100);
  const [reason, setReason] = React.useState("Goodwill credits from BizzAtlas");
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { setCredits(100); setReason("Goodwill credits from BizzAtlas"); }, [user?.id]);
  if (!user) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/grant_credits`, { credits: Number(credits), reason });
      toast.success(`Granted ${credits} credits to ${user.email}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Grant failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="grant-credits-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Grant credits to {user.name}</DialogTitle>
          <DialogDescription>Add free ad credits as a goodwill gesture. A zero-amount invoice is created for auditing.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label htmlFor="gc-credits">Credits</Label><Input id="gc-credits" type="number" min={1} max={100000} value={credits} onChange={(e) => setCredits(e.target.value)} data-testid="grant-credits-amount" /></div>
          <div className="space-y-1"><Label htmlFor="gc-reason">Reason (shown on invoice)</Label><Input id="gc-reason" value={reason} maxLength={240} onChange={(e) => setReason(e.target.value)} data-testid="grant-credits-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !credits} className="btn-brand" data-testid="grant-credits-submit">{busy ? "Granting…" : "Grant"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ open, user, onClose, canChangeRoles = true }) {
  const [form, setForm] = React.useState(user || {});
  React.useEffect(() => { setForm(user || {}); }, [user?.id]);
  if (!user) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    const payload = {
      name: form.name, email: form.email, membership: form.membership,
      ad_credits: form.ad_credits != null ? Number(form.ad_credits) : undefined,
      email_verified: form.email_verified,
    };
    if (canChangeRoles) payload.role = form.role;
    try {
      await api.patch(`/admin/users/${user.id}`, payload);
      toast.success("User updated");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle className="font-display">Edit {user.email}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label htmlFor="eu-name">Name</Label><Input id="eu-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="edit-user-name" /></div>
            <div className="space-y-1"><Label htmlFor="eu-email">Email</Label><Input id="eu-email" type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="edit-user-email" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Role {!canChangeRoles && <span className="text-xs text-zinc-400">(admin only)</span>}</Label>
              <select className="w-full h-9 rounded-md border border-zinc-200 px-3 text-sm disabled:bg-zinc-100 disabled:text-zinc-500" value={form.role || "user"} onChange={(e) => set("role", e.target.value)} disabled={!canChangeRoles} data-testid="edit-user-role">
                <option value="user">user</option>
                <option value="business_owner">business_owner</option>
                <option value="editor">editor</option>
                <option value="manager">manager</option>
                <option value="admin">admin</option>
              </select>
            </div>
            <div className="space-y-1"><Label>Membership</Label>
              <select className="w-full h-9 rounded-md border border-zinc-200 px-3 text-sm" value={form.membership || "free"} onChange={(e) => set("membership", e.target.value)} data-testid="edit-user-membership">
                <option value="free">free</option>
                <option value="pro">pro</option>
                <option value="premium">premium</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label htmlFor="eu-credits">Ad credits</Label><Input id="eu-credits" type="number" value={form.ad_credits ?? 0} onChange={(e) => set("ad_credits", e.target.value)} data-testid="edit-user-credits" /></div>
            <div className="space-y-1 flex items-end gap-2"><Switch checked={!!form.email_verified} onCheckedChange={(v) => set("email_verified", v)} data-testid="edit-user-verified" /><Label className="cursor-pointer">Email verified</Label></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="btn-brand" data-testid="edit-user-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Admin: create a listing on behalf of a registered user
// ---------------------------------------------------------------------------
function AdminCreateForUser({ users, onCreated }) {
  const [open, setOpen] = React.useState(false);
  const [cats, setCats] = React.useState([]);
  const [form, setForm] = React.useState({ owner_email: "", name: "", category_slug: "", email: "", description: "" });
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/businesses", form);
      toast.success(`Created '${data.name}' for ${form.owner_email}`);
      setOpen(false);
      setForm({ owner_email: "", name: "", category_slug: "", email: "", description: "" });
      onCreated && onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="admin-create-for-user">
      <CardContent className="p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md grid place-items-center surface-navy">
            <Plus className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-lg leading-tight">Create a listing on behalf of a user</div>
            <div className="text-xs text-zinc-500">Useful for onboarding-as-a-service. Owner must be a registered user.</div>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} className="btn-brand" data-testid="admin-cfu-open"><Plus className="h-4 w-4 mr-1" />New listing for user</Button>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="admin-cfu-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">New listing for user</DialogTitle>
            <DialogDescription>Pick a registered user and fill in the basics. The listing is created with that user as the owner.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Owner user</Label>
              <select className="w-full h-9 rounded-md border border-zinc-200 px-3 text-sm" value={form.owner_email} onChange={(e) => set("owner_email", e.target.value)} data-testid="admin-cfu-owner">
                <option value="">— pick a user —</option>
                {users.map((u) => <option key={u.id} value={u.email}>{u.name} — {u.email}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label htmlFor="cfu-name">Business name *</Label><Input id="cfu-name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="admin-cfu-name" /></div>
              <div className="space-y-1">
                <Label>Category *</Label>
                <select className="w-full h-9 rounded-md border border-zinc-200 px-3 text-sm" value={form.category_slug} onChange={(e) => set("category_slug", e.target.value)} data-testid="admin-cfu-category">
                  <option value="">— choose —</option>
                  {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1"><Label htmlFor="cfu-email">Business email *</Label><Input id="cfu-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="admin-cfu-email" /></div>
            <div className="space-y-1"><Label htmlFor="cfu-desc">Description</Label><Textarea id="cfu-desc" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="admin-cfu-desc" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !form.owner_email || !form.name || !form.category_slug || !form.email} className="btn-brand" data-testid="admin-cfu-submit">{busy ? "Creating…" : "Create listing"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Coupons CRUD
// ---------------------------------------------------------------------------
function CouponsPanel({ canDelete = true }) {
  const [items, setItems] = React.useState([]);
  const [editing, setEditing] = React.useState(null);
  const [analytics, setAnalytics] = React.useState(null);
  const load = () => api.get("/admin/coupons").then((r) => setItems(r.data));
  React.useEffect(() => { load(); }, []);

  const remove = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    await api.delete(`/admin/coupons/${code}`); toast.success("Deleted"); load();
  };
  const toggleActive = async (c) => {
    await api.patch(`/admin/coupons/${c.code}`, { active: !c.active }); load();
  };

  const newCoupon = () => setEditing({
    _new: true, code: "", discount_type: "percent", value: 10,
    applies_to: ["membership", "ad_credits"], max_redemptions: null,
    expires_at: "", active: true, note: "",
  });

  return (
    <Card data-testid="admin-coupons-panel">
      <CardHeader>
        <CardTitle className="font-display flex items-center justify-between">
          Coupons
          <Button size="sm" onClick={newCoupon} className="btn-brand" data-testid="admin-coupons-new"><Plus className="h-4 w-4 mr-1" />New coupon</Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {items.length === 0 ? (
          <div className="text-sm text-zinc-500 p-6 text-center">No coupons yet. Create one to offer discounts during checkout.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-xs">
              <tr><th className="text-left p-3">Code</th><th className="text-left p-3">Discount</th><th className="text-left p-3">Applies to</th><th className="text-left p-3">Redeemed</th><th className="text-left p-3">Expires</th><th className="text-left p-3">Active</th><th className="text-right p-3">Actions</th></tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.code} className="border-t border-zinc-100" data-testid={`admin-coupon-${c.code}`}>
                  <td className="p-3 font-mono-tag font-semibold">{c.code}</td>
                  <td className="p-3">{c.discount_type === "percent" ? `${c.value}% off` : `$${(c.value/100).toFixed(2)} off`}</td>
                  <td className="p-3 text-xs">{(c.applies_to || []).join(", ")}</td>
                  <td className="p-3 font-mono-tag text-xs">{c.redemptions_count ?? 0}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}</td>
                  <td className="p-3 font-mono-tag text-xs text-zinc-500">{c.expires_at?.slice(0, 10) || "—"}</td>
                  <td className="p-3"><Switch checked={!!c.active} onCheckedChange={() => toggleActive(c)} data-testid={`admin-coupon-active-${c.code}`} /></td>
                  <td className="p-3 text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => setAnalytics(c)} data-testid={`admin-coupon-stats-${c.code}`}>Stats</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...c })} data-testid={`admin-coupon-edit-${c.code}`}><Pencil className="h-4 w-4" /></Button>
                    {canDelete && (
                      <Button size="sm" variant="outline" onClick={() => remove(c.code)} data-testid={`admin-coupon-del-${c.code}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
      <CouponEditor open={!!editing} coupon={editing} onClose={() => { setEditing(null); load(); }} />
      <CouponAnalyticsDialog open={!!analytics} coupon={analytics} onClose={() => setAnalytics(null)} />
    </Card>
  );
}

function CouponAnalyticsDialog({ open, coupon, onClose }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    if (!open || !coupon) { setData(null); return; }
    api.get(`/admin/coupons/${coupon.code}/analytics`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [open, coupon?.code]);
  if (!coupon) return null;
  const fmt = (c) => `$${(c / 100).toFixed(2)}`;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="coupon-analytics-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Analytics — {coupon.code}</DialogTitle>
          <DialogDescription>Redemption funnel and revenue over time.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-10 text-center text-sm text-zinc-400 font-mono-tag">Loading…</div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg border border-zinc-200 p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-mono-tag">Redemptions</div>
                <div className="font-display text-2xl">{data.redemptions_count}{data.max_redemptions ? <span className="text-zinc-400 text-base"> / {data.max_redemptions}</span> : null}</div>
              </div>
              <div className="rounded-lg border border-zinc-200 p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-mono-tag">Revenue</div>
                <div className="font-display text-2xl">{fmt(data.total_revenue_cents)}</div>
              </div>
              <div className="rounded-lg border border-zinc-200 p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-mono-tag">Discount given</div>
                <div className="font-display text-2xl">{fmt(data.total_discount_cents)}</div>
              </div>
              <div className="rounded-lg border border-zinc-200 p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-mono-tag">Paid invoices</div>
                <div className="font-display text-2xl">{data.invoices_count}</div>
              </div>
            </div>
            {data.timeseries.length > 0 ? (
              <div className="rounded-lg border border-zinc-200 p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-mono-tag mb-2">Daily redemptions</div>
                <table className="w-full text-xs">
                  <thead className="text-zinc-500"><tr><th className="text-left p-1">Date</th><th className="text-left p-1">Redemptions</th><th className="text-left p-1">Revenue</th><th className="text-left p-1">Discount</th></tr></thead>
                  <tbody>
                    {data.timeseries.map((d) => (
                      <tr key={d.date} className="border-t border-zinc-100">
                        <td className="p-1 font-mono-tag">{d.date}</td>
                        <td className="p-1">{d.redemptions}</td>
                        <td className="p-1">{fmt(d.revenue_cents)}</td>
                        <td className="p-1">{fmt(d.discount_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-sm text-zinc-500 text-center py-6">No paid redemptions yet.</div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CouponEditor({ open, coupon, onClose }) {
  const [form, setForm] = React.useState(coupon || {});
  React.useEffect(() => { setForm(coupon || {}); }, [coupon?.code, coupon?._new]);
  if (!coupon) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleApplies = (k) => {
    const cur = form.applies_to || [];
    set("applies_to", cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);
  };
  const submit = async () => {
    const payload = {
      discount_type: form.discount_type,
      value: Number(form.value),
      applies_to: form.applies_to || ["membership", "ad_credits"],
      max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
      expires_at: form.expires_at || null,
      active: !!form.active,
      note: form.note || "",
    };
    try {
      if (form._new) {
        await api.post("/admin/coupons", { code: form.code.toUpperCase().trim(), ...payload });
        toast.success("Coupon created");
      } else {
        await api.patch(`/admin/coupons/${form.code}`, payload);
        toast.success("Coupon updated");
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="coupon-editor">
        <DialogHeader>
          <DialogTitle className="font-display">{form._new ? "New coupon" : `Edit ${form.code}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Code</Label><Input value={form.code || ""} disabled={!form._new} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="LAUNCH50" data-testid="coupon-code" /></div>
            <div className="space-y-1"><Label>Discount type</Label>
              <select className="w-full h-9 rounded-md border border-zinc-200 px-3 text-sm" value={form.discount_type} onChange={(e) => set("discount_type", e.target.value)} data-testid="coupon-type">
                <option value="percent">Percent off</option>
                <option value="fixed">Fixed amount off (cents)</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Value {form.discount_type === "percent" ? "(1–100)" : "(in cents)"}</Label><Input type="number" min={1} value={form.value ?? 10} onChange={(e) => set("value", e.target.value)} data-testid="coupon-value" /></div>
            <div className="space-y-1"><Label>Max redemptions (blank = unlimited)</Label><Input type="number" min={1} value={form.max_redemptions ?? ""} onChange={(e) => set("max_redemptions", e.target.value)} data-testid="coupon-max" /></div>
          </div>
          <div className="space-y-1">
            <Label>Applies to</Label>
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={(form.applies_to || []).includes("membership")} onChange={() => toggleApplies("membership")} data-testid="coupon-applies-membership" />
                Memberships
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={(form.applies_to || []).includes("ad_credits")} onChange={() => toggleApplies("ad_credits")} data-testid="coupon-applies-credits" />
                Ad credits
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Expires at (ISO)</Label><Input value={form.expires_at || ""} onChange={(e) => set("expires_at", e.target.value)} placeholder="2026-12-31T23:59:59Z" data-testid="coupon-expires" /></div>
            <div className="space-y-1 flex items-end gap-2"><Switch checked={!!form.active} onCheckedChange={(v) => set("active", v)} data-testid="coupon-active" /><Label className="cursor-pointer">{form.active ? "Active" : "Disabled"}</Label></div>
          </div>
          <div className="space-y-1"><Label>Internal note</Label><Input value={form.note || ""} onChange={(e) => set("note", e.target.value)} placeholder="What's this for?" data-testid="coupon-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="btn-brand" data-testid="coupon-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SiteSettingsPanel({ readOnly = false }) {
  const [s, setS] = React.useState(null);
  const load = () => api.get("/admin/site_settings").then((r) => setS(r.data));
  React.useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.patch("/admin/site_settings", {
        head_scripts: s.head_scripts || "",
        footer_scripts: s.footer_scripts || "",
        google_analytics_id: s.google_analytics_id || "",
      });
      toast.success("Settings saved. Refresh the site to see scripts load.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  if (!s) return <Card><CardContent className="p-6 text-sm text-zinc-500">Loading…</CardContent></Card>;
  return (
    <Card data-testid="admin-site-settings">
      <CardHeader>
        <CardTitle className="font-display">Site settings</CardTitle>
        {readOnly && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2" data-testid="ss-readonly-notice">
            Read-only view. Only admins can change site settings.
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="ss-ga">Google Analytics measurement ID</Label>
          <Input id="ss-ga" value={s.google_analytics_id || ""} onChange={(e) => setS({ ...s, google_analytics_id: e.target.value })} placeholder="G-XXXXXXXXXX" data-testid="ss-ga" readOnly={readOnly} disabled={readOnly} />
          <div className="text-xs text-zinc-500">Pasting a GA4 ID auto-injects gtag.js across the public site.</div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ss-head">Custom &lt;head&gt; scripts</Label>
          <Textarea id="ss-head" rows={8} value={s.head_scripts || ""} onChange={(e) => setS({ ...s, head_scripts: e.target.value })} placeholder="Paste HTML: meta tags, gtag, Hotjar, Segment, etc." data-testid="ss-head" readOnly={readOnly} disabled={readOnly} />
          <div className="text-xs text-zinc-500">Pasted HTML is appended to &lt;head&gt; on every page. Use for site-verification meta tags, analytics, A/B tools, etc.</div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ss-foot">Footer scripts (optional)</Label>
          <Textarea id="ss-foot" rows={5} value={s.footer_scripts || ""} onChange={(e) => setS({ ...s, footer_scripts: e.target.value })} data-testid="ss-foot" readOnly={readOnly} disabled={readOnly} />
        </div>
        {!readOnly && (
          <Button onClick={save} className="btn-brand" data-testid="ss-save">Save settings</Button>
        )}
      </CardContent>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// Business edit dialog — staff (admin/manager/editor) can edit core listing fields
// ---------------------------------------------------------------------------
function BusinessEditDialog({ biz, onClose, onSaved }) {
  const [form, setForm] = React.useState(biz || {});
  const [cats, setCats] = React.useState([]);
  React.useEffect(() => { setForm(biz || {}); }, [biz?.id]);
  React.useEffect(() => {
    if (!biz) return;
    api.get("/categories").then((r) => setCats(r.data)).catch(() => setCats([]));
  }, [biz?.id]);
  if (!biz) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    try {
      await api.patch(`/businesses/${biz.id}`, {
        name: form.name,
        description: form.description,
        category_slug: form.category_slug,
        address: form.address,
        phone: form.phone,
        email: form.email,
        website: form.website,
        logo: form.logo,
      });
      toast.success("Listing updated");
      onClose();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };
  return (
    <Dialog open={!!biz} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="edit-biz-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit listing</DialogTitle>
          <DialogDescription>Edit core content for <span className="font-mono-tag">{biz.slug || biz.id}</span></DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1 col-span-2"><Label htmlFor="eb-name">Name</Label><Input id="eb-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="edit-biz-name" /></div>
          <div className="space-y-1 col-span-2"><Label htmlFor="eb-desc">Description</Label><Textarea id="eb-desc" rows={4} value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="edit-biz-desc" /></div>
          <div className="space-y-1"><Label>Category</Label>
            <select className="w-full h-9 rounded-md border border-zinc-200 px-3 text-sm" value={form.category_slug || ""} onChange={(e) => set("category_slug", e.target.value)} data-testid="edit-biz-cat">
              {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <div className="space-y-1"><Label htmlFor="eb-addr">Address</Label><Input id="eb-addr" value={form.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="edit-biz-addr" /></div>
          <div className="space-y-1"><Label htmlFor="eb-phone">Phone</Label><Input id="eb-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="edit-biz-phone" /></div>
          <div className="space-y-1"><Label htmlFor="eb-email">Email</Label><Input id="eb-email" type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="edit-biz-email" /></div>
          <div className="space-y-1"><Label htmlFor="eb-web">Website</Label><Input id="eb-web" value={form.website || ""} onChange={(e) => set("website", e.target.value)} data-testid="edit-biz-web" /></div>
          <div className="space-y-1"><Label htmlFor="eb-logo">Logo URL</Label><Input id="eb-logo" value={form.logo || ""} onChange={(e) => set("logo", e.target.value)} data-testid="edit-biz-logo" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="btn-brand" data-testid="edit-biz-save"><Save className="h-4 w-4 mr-1" />Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
